import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSessionUser } from '@/hooks/auth/useSessionUser';
import { restoreSession, persistSession } from '@/services/auth';
import { authActions } from '@/redux/auth/authSlice';
import { checkProductAccess } from '@/utils/accountsApi';
import NoProductAccess from '@/components/ui/NoProductAccess';
import { MorphingSquare } from '@/components/ui/morphing-square';

// Keeps the loader visible briefly on a full page load, even when the session/product cache resolves instantly.
const BOOT_MIN_MS = 800;
let bootStarted = 0;

type PrivateRouteProps = { children: ReactElement };

const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const { isLoggedIn } = useSessionUser();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isResolvingSession, setIsResolvingSession] = useState(!isLoggedIn);
  const [productAccess, setProductAccess] = useState<boolean | null>(null);
  const productCheckedRef = useRef(false);
  const [bootDone, setBootDone] = useState(() => bootStarted !== 0);

  useEffect(() => {
    if (bootDone) return;
    bootStarted = Date.now();
    const t = setTimeout(() => setBootDone(true), BOOT_MIN_MS);
    return () => clearTimeout(t);
  }, [bootDone]);

  const PRODUCT_KEY = 'Calendar';
  const PRODUCT_CACHE_KEY = 'sculpx_product_access_calendar';
  const PRODUCT_CACHE_TTL_MS = 10 * 60 * 1000;

  const readProductCache = (): boolean | null => {
    try {
      const raw = sessionStorage.getItem(PRODUCT_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { accessible: boolean; at: number };
      if (typeof parsed.accessible !== 'boolean' || typeof parsed.at !== 'number') return null;
      if (Date.now() - parsed.at > PRODUCT_CACHE_TTL_MS) return null;
      return parsed.accessible;
    } catch { return null; }
  };

  const writeProductCache = (accessible: boolean): void => {
    try { sessionStorage.setItem(PRODUCT_CACHE_KEY, JSON.stringify({ accessible, at: Date.now() })); }
    catch { /* ignore */ }
  };

  // Session restore: runs only when isLoggedIn is false.
  // NO location deps — route changes must never trigger a fresh /auth/refresh call.
  useEffect(() => {
    if (isLoggedIn) {
      setIsResolvingSession(false);
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      const restored = await restoreSession();
      if (cancelled) return;
      if (restored?.accessToken) {
        persistSession(restored);
        dispatch(authActions.hydrateFromSession());
        return;
      }
      setIsResolvingSession(false);
    };

    void resolve();
    return () => { cancelled = true; };
  }, [dispatch, isLoggedIn]);

  // Product access check: runs once when the user becomes logged in
  useEffect(() => {
    if (!isLoggedIn) {
      setProductAccess(null);
      productCheckedRef.current = false;
      return;
    }
    if (productCheckedRef.current) return;
    productCheckedRef.current = true;

    const cached = readProductCache();
    if (cached !== null) { setProductAccess(cached); return; }

    checkProductAccess(PRODUCT_KEY)
      .then((result) => {
        const accessible = !!result?.accessible;
        writeProductCache(accessible);
        setProductAccess(accessible);
      })
      .catch(() => {
        setProductAccess(readProductCache() ?? true);
      });
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    if (isResolvingSession) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <MorphingSquare message="Checking session..." />
        </div>
      );
    }
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${from}`} replace />;
  }

  if (productAccess === null || !bootDone) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <MorphingSquare message="Loading calendar..." />
      </div>
    );
  }

  if (!productAccess) return <NoProductAccess />;

  return children;
};

export default PrivateRoute;
